import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import NewsArticleForm from './admin/NewsArticleForm';

import NewsArticleTable from './admin/NewsArticleTable';

import {

  adminFetch,

  handleAdminAuthError,

  logoutAdmin,

} from './admin/adminAuth';

import {

  API_URL,

  articleToFormData,

  EMPTY_ARTICLE_FORM,

  formDataToPayload,

  validateArticleForm,

} from './admin/newsConfig';



export default function AdminNewsPage() {

  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [successMessage, setSuccessMessage] = useState('');

  const [formMode, setFormMode] = useState('create');

  const [currentArticleId, setCurrentArticleId] = useState(null);

  const [formData, setFormData] = useState(EMPTY_ARTICLE_FORM);

  const [formSubmitting, setFormSubmitting] = useState(false);

  const [formError, setFormError] = useState(null);

  const [sourceFilter, setSourceFilter] = useState('all');

  const [categoryFilter, setCategoryFilter] = useState('all');

  const [loggingOut, setLoggingOut] = useState(false);



  const fetchArticles = async () => {

    try {

      setLoading(true);

      const res = await adminFetch(`${API_URL}/admin/news?limit=100`);

      if (handleAdminAuthError(res, navigate)) return;

      if (!res.ok) {

        const errorData = await res.json().catch(() => ({}));

        throw new Error(errorData.detail || 'Failed to fetch articles');

      }

      const data = await res.json();

      setArticles(data);

      setError(null);

    } catch (err) {

      setError(err.message);

    } finally {

      setLoading(false);

    }

  };



  useEffect(() => {

    fetchArticles();

  }, []);



  useEffect(() => {

    if (!successMessage) return undefined;

    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 4000);

    return () => window.clearTimeout(timeoutId);

  }, [successMessage]);



  const handleLogout = async () => {

    setLoggingOut(true);

    try {

      await logoutAdmin(API_URL);

    } finally {

      setLoggingOut(false);

      navigate('/admin/login', { replace: true });

    }

  };



  const handleInputChange = (e) => {

    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({

      ...prev,

      [name]: type === 'checkbox' ? checked : value,

    }));

  };



  const resetForm = () => {

    setFormMode('create');

    setCurrentArticleId(null);

    setFormData(EMPTY_ARTICLE_FORM);

    setFormError(null);

  };



  const handleEdit = (article) => {

    setFormMode('edit');

    setCurrentArticleId(article.id);

    setFormData(articleToFormData(article));

    setFormError(null);

    window.scrollTo({ top: 0, behavior: 'smooth' });

  };



  const handleSubmit = async (e) => {

    e.preventDefault();

    setFormSubmitting(true);

    setFormError(null);



    const validationErrors = validateArticleForm(formData);

    if (validationErrors.length > 0) {

      setFormError(validationErrors.join('\n'));

      setFormSubmitting(false);

      return;

    }



    try {

      const payload = formDataToPayload(formData);

      const url =

        formMode === 'create'

          ? `${API_URL}/news`

          : `${API_URL}/news/${encodeURIComponent(currentArticleId)}`;



      const res = await adminFetch(url, {

        method: formMode === 'create' ? 'POST' : 'PUT',

        body: JSON.stringify({ data: payload }),

      });



      if (handleAdminAuthError(res, navigate)) return;

      if (!res.ok) {

        const errorData = await res.json().catch(() => ({}));

        const detail = errorData.detail;

        if (Array.isArray(detail)) {

          throw new Error(detail.map((item) => item.msg || JSON.stringify(item)).join('\n'));

        }

        throw new Error(detail || 'Failed to save article');

      }



      resetForm();

      setSuccessMessage(

        formMode === 'create' ? 'Article created successfully.' : 'Article updated successfully.'

      );

      fetchArticles();

    } catch (err) {

      setFormError(err.message);

    } finally {

      setFormSubmitting(false);

    }

  };



  const handleDelete = async (articleId) => {

    if (!window.confirm('Are you sure you want to delete this article?')) return;



    try {

      const res = await adminFetch(`${API_URL}/news/${encodeURIComponent(articleId)}`, {

        method: 'DELETE',

      });



      if (handleAdminAuthError(res, navigate)) return;

      if (!res.ok) {

        const errorData = await res.json().catch(() => ({}));

        throw new Error(errorData.detail || 'Failed to delete article');

      }



      if (currentArticleId === articleId) {

        resetForm();

      }

      setSuccessMessage('Article deleted successfully.');

      fetchArticles();

    } catch (err) {

      alert(`Error deleting article: ${err.message}`);

    }

  };



  return (

    <div className="min-h-screen bg-cpre-pebble/20">

      <div className="max-w-6xl mx-auto px-4 py-8">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">

          <div>

            <h1 className="text-3xl font-heading font-bold text-cpre-new-yew">News Admin Panel</h1>

            <p className="text-sm text-muted-foreground mt-1">

              Manage Local and National news for the public News page.

            </p>

          </div>

          <div className="flex flex-wrap gap-2">

            <button

              type="button"

              onClick={() => navigate('/news')}

              className="px-4 py-2 bg-cpre-new-yew text-white rounded-full hover:bg-cpre-hill"

            >

              View Public News

            </button>

            <button

              type="button"

              onClick={handleLogout}

              disabled={loggingOut}

              className="px-4 py-2 border border-stone-300 text-cpre-new-yew rounded-full hover:bg-stone-50 disabled:opacity-60"

            >

              {loggingOut ? 'Signing out…' : 'Log out'}

            </button>

          </div>

        </div>



        {error && (

          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">

            Error: {error}

          </div>

        )}



        {successMessage && (

          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">

            {successMessage}

          </div>

        )}



        <NewsArticleForm

          formMode={formMode}

          formData={formData}

          formError={formError}

          formSubmitting={formSubmitting}

          onChange={handleInputChange}

          onFormDataChange={setFormData}

          onSubmit={handleSubmit}

          onCancel={resetForm}

        />



        <NewsArticleTable

          articles={articles}

          loading={loading}

          sourceFilter={sourceFilter}

          categoryFilter={categoryFilter}

          onSourceFilterChange={setSourceFilter}

          onCategoryFilterChange={setCategoryFilter}

          onEdit={handleEdit}

          onDelete={handleDelete}

        />

      </div>

    </div>

  );

}


